import { Filter, X } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover'

/**
 * Row of "applied filter" pills shown above a Settings data table. Each pill names
 * the filter and its value and carries its own ✕ to clear just that filter;
 * `onClearAll` renders a trailing "Clear all" link. Renders nothing when no
 * filters are applied.
 *
 * `pills`: [{ key, label, value, onClear }]
 */
export const AppliedFilterPills = ({ pills = [], onClearAll }) => {
  if (pills.length === 0) return null
  return (
    <div className='mb-3 flex flex-wrap items-center gap-2'>
      {pills.map((pill) => (
        <span
          key={pill.key}
          className='inline-flex items-center gap-1.5 rounded-full border border-[#BFDBFE] bg-[#EFF6FF] py-1 pl-3 pr-1.5 text-[12px] text-[#1E40AF]'
        >
          <span className='text-[#3B82F6]'>{pill.label}:</span>
          <span className='max-w-[180px] truncate font-medium' title={pill.value}>
            {pill.value}
          </span>
          <button
            type='button'
            onClick={pill.onClear}
            aria-label={`Clear ${pill.label} filter`}
            className='flex h-4 w-4 items-center justify-center rounded-full text-[#3B82F6] hover:bg-[#DBEAFE] hover:text-[#1E40AF]'
          >
            <X className='h-3 w-3' />
          </button>
        </span>
      ))}
      {onClearAll && (
        <button type='button' onClick={onClearAll} className='text-[12px] font-medium text-[#6B7280] hover:text-[#0A1628] hover:underline'>
          Clear all
        </button>
      )}
    </div>
  )
}

/** Labelled field wrapper for controls inside a FiltersPopover. */
export const FilterField = ({ label, children }) => (
  <div className='flex flex-col gap-1.5'>
    <span className='text-[12px] font-medium text-[#475569]'>{label}</span>
    {children}
  </div>
)

/**
 * Shared "Filters" toolbar button for the Settings data tables (Rules, Mappings).
 * Collapses the secondary filter controls into a popover so the toolbar stays a
 * single search box + this button. `activeCount` (filters set to a non-default
 * value) renders as a badge on the trigger.
 */
export const FiltersPopover = ({ activeCount = 0, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <Button variant='outline' size='sm' className='bg-white'>
        <Filter className='mr-1.5 h-4 w-4' />
        Filters
        {activeCount > 0 && (
          <span className='ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#1F6FEB] px-1.5 text-[11px] font-semibold text-white'>
            {activeCount}
          </span>
        )}
      </Button>
    </PopoverTrigger>
    <PopoverContent align='start' className='flex max-h-[70vh] w-[280px] flex-col gap-3 overflow-y-auto'>
      {children}
    </PopoverContent>
  </Popover>
)
